<script setup>
/**
 * 按用户排行：横向条形，烧钱最多的在最上面。点一根条 = 下钻到这个用户的调用日志。
 *
 * `user_id` 为 null 的那一组是**没有用户归属的调用**（后端不编一个假用户）。它照样画出来、照样计进
 * 成本，但**没有下钻入口** —— 调用日志按 `user_id` 筛不出「没有 user_id」的那批。
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EChart from '../../../components/EChart.vue'
import { categoryAxis, chartBase, chartTheme, valueAxis } from '../../../components/chart-theme.js'
import { useAdminTheme } from '../../../composables/useAdminTheme.js'
import { formatInt, formatMoney } from '../llm-format.js'

const props = defineProps({
  /** @type {import('vue').PropType<import('../../../api/llm/index.js').CostUserSlice[]>} */
  items: { type: Array, required: true },
})

const emit = defineEmits(['select'])

const { t } = useI18n()
// 主题读进 computed：切明暗时 option 才会重算（ECharts 不认 CSS 变量）。
const { theme: themeName } = useAdminTheme()

const rows = computed(() => [...props.items].reverse())

/** @param {import('../../../api/llm/index.js').CostUserSlice} item */
function labelOf(item) {
  return item.user_id ?? t('visualGameAdmin.llm.cost.user.anonymous')
}

const option = computed(() => {
  const theme = chartTheme(themeName.value)
  return {
    ...chartBase(theme),
    grid: { top: 8, left: 8, right: 72, bottom: 8, containLabel: true },
    // 成本轴一律是钱（`cost-metrics.js` 是唯一的格式来源）。
    xAxis: { ...valueAxis(theme), axisLabel: { color: theme.textMuted, formatter: formatMoney } },
    yAxis: { ...categoryAxis(theme), data: rows.value.map(labelOf) },
    tooltip: { ...chartBase(theme).tooltip, formatter: tooltipOf },
    series: [
      {
        type: 'bar',
        name: t('visualGameAdmin.llm.cost.metric.cost'),
        barMaxWidth: 22,
        itemStyle: { color: theme.bar, borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: 'right', color: theme.textMuted, formatter: (p) => formatMoney(p.value) },
        data: rows.value.map((item) => item.supplier_cost_micros),
      },
    ],
  }
})

/** @param {any[]} params */
function tooltipOf(params) {
  const item = rows.value[params[0].dataIndex]
  const lines = [
    `<strong>${labelOf(item)}</strong>`,
    `${t('visualGameAdmin.llm.cost.metric.cost')}: ${formatMoney(item.supplier_cost_micros)}`,
    `${t('visualGameAdmin.llm.cost.metric.calls')}: ${formatInt(item.calls)}`,
    `${t('visualGameAdmin.llm.cost.metric.charged_points')}: ${formatInt(item.charged_points)}`,
  ]
  if (item.unpriced_calls > 0) {
    lines.push(`⚠ ${t('visualGameAdmin.llm.cost.unpriced.short', { count: item.unpriced_calls })}`)
  }
  lines.push(
    item.user_id === null
      ? t('visualGameAdmin.llm.cost.user.noDrilldown')
      : t('visualGameAdmin.llm.cost.chart.clickToCalls'),
  )
  return lines.join('<br/>')
}

function onSelect({ index }) {
  const item = rows.value[index]
  if (item.user_id === null) return
  emit('select', item)
}
</script>

<template>
  <EChart
    :option="option"
    :height="`${Math.max(200, rows.length * 44 + 40)}px`"
    @select="onSelect"
  />
</template>
